import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Send, FileDown, Handshake, CalendarDays, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import ImpegniChiusura from "@/components/ImpegniChiusura";

const MGMT = ["admin", "back_office", "commercial_manager", "head_of_partner"];
const eur = (v) => `${Math.round(Number(v) || 0).toLocaleString("it-IT")} €`;
const chip = (p) => p == null ? "bg-muted text-muted-foreground" : p >= 100 ? "bg-emerald-500/15 text-emerald-400" : p > 70 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400";
const PctChip = ({ p }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${chip(p)}`}>{p == null ? "n/d" : `${p}%`}</span>;

export default function ImpegniOverview({ sel, setSel, agencies = [] }) {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [rep, setRep] = useState({ week: true, month: true });
  const [mode, setMode] = useState("aggregate");
  const [sender, setSender] = useState("back_office");
  const [ccRoles, setCcRoles] = useState({ back_office: false, direzione: false, responsabile: false });
  const [toRoles, setToRoles] = useState({ back_office: false, direzione: true, responsabile: false });
  const [toFree, setToFree] = useState("");
  const [ccFree, setCcFree] = useState("");

  useEffect(() => { setOv(null); api.get(`/agency-commitments/overview?agency_id=${sel}`).then((r) => setOv(r.data)).catch(() => setOv(false)); }, [sel]);
  const [rmails, setRmails] = useState({});
  useEffect(() => { api.get("/email-recipients/roles").then((r) => setRmails(r.data || {})).catch(() => {}); }, []);
  const em = (k) => (rmails[k] && rmails[k].length ? ` (${rmails[k].join(", ")})` : "");

  if (!MGMT.includes(user?.role)) return null;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/agency-commitments/pdf?agency_id=${sel}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = sel === "all" ? "riunione_settimanale_rete.pdf" : "riunione_settimanale.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download PDF non riuscito"); } finally { setPdfBusy(false); }
  };

  const doSend = async () => {
    const reports = Object.keys(rep).filter((k) => rep[k]);
    if (!reports.length) { toast.error("Seleziona almeno un report"); return; }
    setSending(true);
    try {
      const body = {
        agency_id: sel, reports, sender,
        cc_roles: Object.keys(ccRoles).filter((k) => ccRoles[k]),
        cc_emails: ccFree.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@")),
      };
      if (sel === "all") {
        body.mode = mode;
        if (mode === "aggregate") {
          body.to_roles = Object.keys(toRoles).filter((k) => toRoles[k]);
          body.to_emails = toFree.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
        }
      }
      const r = await api.post("/agency-commitments/send", body);
      toast.success(r.data.mode === "per_agency" ? `Inviate ${r.data.agenzie} email alle agenzie` : `Email inviata a ${r.data.inviate} destinatario/i`);
      setSendOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };

  return (
    <Card className="p-5" data-testid="impegni-overview">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight">Impegni settimanali e mensili</h2>
            <p className="text-[11px] text-muted-foreground">Impegni di chiusura per agenzia · settimana e mese correnti</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-9 w-[240px]" data-testid="impegni-ov-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="impegni-ov-pdf">{pdfBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />} PDF Riunione Settimanale</Button>
        </div>
      </div>

      {sel !== "all" ? (
        <ImpegniChiusura agencyId={sel} canEdit />
      ) : ov === null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</div>
      ) : ov ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[["week", "Impegni di chiusura · settimana", CalendarDays], ["month", "Impegno mensile", CalendarRange]].map(([tipo, title, Icon]) => {
            const rows = (ov.agencies || []).flatMap((a) => (a[tipo].righe || []).map((r) => ({ ...r, agency: a.agency_name })));
            const t = ov.totali[tipo];
            return (
              <Card key={tipo} className="border-primary/40 bg-primary/[0.06] p-4" data-testid={`impegni-ov-slot-${tipo}`}>
                <div className="mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><h3 className="font-heading text-sm font-black leading-tight">{title}</h3></div>
                <div className="max-h-[360px] space-y-1.5 overflow-y-auto pr-1">
                  {rows.length === 0 && <p className="text-xs text-muted-foreground">Nessun impegno inserito.</p>}
                  {rows.map((r, i) => {
                    const n = r.riportato_count || 1; const hot = n >= 3;
                    return (
                    <div key={i} className={`flex items-center justify-between gap-2 border-b border-border/40 pb-1.5 text-sm ${r.barrato ? "opacity-60" : ""}`} data-testid={`impegni-ov-line-${tipo}-${i}`}>
                      <span className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className={`truncate ${r.barrato ? "line-through" : ""}`}>{r.cliente || "—"}</span>
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.agency}</span>
                        {r.riportato && !r.barrato && (
                          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${hot ? "bg-red-500/20 text-red-500" : "bg-amber-500/20 text-amber-500"}`} title={`Impegno rimandato ${n} ${n === 1 ? "volta" : "volte"} dai periodi precedenti`} data-testid={`impegni-ov-riportato-${tipo}-${i}`}>Riportato{n > 1 ? ` ×${n}` : ""}</span>
                        )}
                        {r.barrato && (
                          <span className="shrink-0 rounded bg-zinc-500/20 px-1.5 py-0.5 text-[10px] font-bold text-zinc-400" data-testid={`impegni-ov-barrato-${tipo}-${i}`}>Barrato</span>
                        )}
                      </span>
                      <span className={`shrink-0 font-mono ${r.barrato ? "line-through opacity-60" : ""}`}>{eur(r.importo_eur)}</span>
                    </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-3 text-sm">
                  <span className="text-muted-foreground">Totale <b className="font-mono text-foreground">{eur(t.totale_eur)}</b> / obiettivo <b className="font-mono text-foreground">{eur(t.obiettivo_eur)}</b></span>
                  <PctChip p={t.pct} />
                </div>
              </Card>
            );
          })}
        </div>
      ) : <p className="py-4 text-sm text-muted-foreground">Dati non disponibili.</p>}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md" data-testid="impegni-ov-send">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Invia impegni via email</DialogTitle>
            <DialogDescription>{sel === "all" ? "Selezione: tutte le agenzie" : "Selezione: agenzia singola (invio agli agenti)"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-primary/30 bg-primary/[0.06] p-3 text-[12px] leading-snug text-muted-foreground" data-testid="ov-send-note">
              {sel !== "all"
                ? "L'email riepiloga gli impegni (settimana/mese) dell'agenzia selezionata e viene inviata ai suoi agenti."
                : mode === "per_agency"
                  ? "Invio massivo: un'email distinta per ciascuna agenzia, con i soli impegni di quell'agenzia, inviata ai rispettivi agenti."
                  : "Email unica aggregata: un solo messaggio con gli impegni di tutte le agenzie, inviato ai destinatari indicati in \u201cA\u201d."}
              {" "}Il mittente (Da) definisce firma e indirizzo di risposta; i contatti in CC ricevono copia per conoscenza.
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase text-muted-foreground">Report da inviare</Label>
              <div className="mt-1.5 flex gap-5">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={rep.week} onCheckedChange={(v) => setRep((s) => ({ ...s, week: !!v }))} data-testid="ov-rep-week" /> Settimanale</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={rep.month} onCheckedChange={(v) => setRep((s) => ({ ...s, month: !!v }))} data-testid="ov-rep-month" /> Mensile</label>
              </div>
            </div>
            {sel === "all" && (
              <div>
                <Label className="text-[11px] font-bold uppercase text-muted-foreground">Modalità</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="mt-1.5 h-9" data-testid="ov-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aggregate">Email unica aggregata (a Direzione/BO/indirizzi)</SelectItem>
                    <SelectItem value="per_agency">Invio massivo: a ogni agenzia i propri impegni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px] font-bold uppercase text-muted-foreground">Mittente (Da)</Label>
              <Select value={sender} onValueChange={setSender}>
                <SelectTrigger className="mt-1.5 h-9" data-testid="ov-sender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="back_office">Back Office{em("back_office")}</SelectItem>
                  <SelectItem value="direzione">Direzione{em("direzione")}</SelectItem>
                  <SelectItem value="responsabile">Responsabile Commerciale{em("responsabile")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sel === "all" && mode === "aggregate" && (
              <div>
                <Label className="text-[11px] font-bold uppercase text-muted-foreground">Destinatari (A)</Label>
                <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.direzione} onCheckedChange={(v) => setToRoles((s) => ({ ...s, direzione: !!v }))} data-testid="ov-to-dir" /> Direzione{em("direzione")}</label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.back_office} onCheckedChange={(v) => setToRoles((s) => ({ ...s, back_office: !!v }))} data-testid="ov-to-bo" /> Back Office{em("back_office")}</label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.responsabile} onCheckedChange={(v) => setToRoles((s) => ({ ...s, responsabile: !!v }))} data-testid="ov-to-resp" /> Responsabile{em("responsabile")}</label>
                </div>
                <Input value={toFree} onChange={(e) => setToFree(e.target.value)} placeholder="Email destinatari (separate da virgola)" className="mt-2 h-9" data-testid="ov-to-free" />
              </div>
            )}
            <div>
              <Label className="text-[11px] font-bold uppercase text-muted-foreground">In copia (CC)</Label>
              <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.back_office} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, back_office: !!v }))} /> Back Office{em("back_office")}</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.direzione} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, direzione: !!v }))} /> Direzione{em("direzione")}</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.responsabile} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, responsabile: !!v }))} /> Responsabile{em("responsabile")}</label>
              </div>
              <Input value={ccFree} onChange={(e) => setCcFree(e.target.value)} placeholder="Email in copia (separate da virgola)" className="mt-2 h-9" data-testid="ov-cc-free" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending} data-testid="ov-send-submit">{sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
